import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SectionDataDB } from '../../../data/datatypes';
import { getSection } from '../../../services/DBService/DbService';
import LoadingScreen from '../../atoms/LoadingScreen';
import SectionItem from './SectionItem';
import { getOrderedContentIds } from '../../../utils/contentOrder';

type Properties = {
    sectionId?: `section_${string}`;    // if not specified, gets sectionId from url
    navigateNext: () => void;
    navigatePrevious?: () => void;
    showDoneButtonAfterSection?: boolean;
};

const Section = (props: Properties) => {
    const navigate = useNavigate();
    const { itemId } = useParams<'itemId'>();
    const [section, setSection] = React.useState<SectionDataDB>();

    const paramsSectionId = useParams().sectionId;
    const sectionId = props.sectionId || paramsSectionId;
    if (!sectionId) {
        throw 'No sectionId found';
    }
    if (!(typeof sectionId.startsWith === 'function') || !sectionId.startsWith('section_')) {
        throw 'Invalid sectionId';
    }

    React.useEffect(() => {
        fetchSection(sectionId as `section_${string}`);
    }, [sectionId]);

    const fetchSection = async (id: `section_${string}`) => {
        const sectionData = await getSection(id);
        setSection(sectionData);

        const sectionItemIds = getOrderedContentIds(Object.keys(sectionData.items));
        if (sectionData !== undefined && sectionItemIds.length > 0) {
            if (!itemId) {
                navigate(sectionItemIds[0], { replace: true });
            } else if (itemId === 'last') {
                navigate(`../${sectionItemIds[sectionItemIds.length - 1]}`, { replace: true });
            }
        }
    };

    if (!section || !itemId || !Object.keys(section.items).includes(itemId)) {
        return <LoadingScreen message="Lade Inhalte...  Wenn die Inhalte nicht laden, starte bitte die App einmal neu. Es kann auch sein, dass Du auf alte Inhalte zugreifen willst, die nicht mehr Bestandteil der App sind." />;
    }

    const itemIds = getOrderedContentIds(Object.keys(section.items));
    const currentIndex = itemIds.indexOf(itemId);

    const goToNextItem = () => {
        if (currentIndex === itemIds.length - 1) {
            props.navigateNext();
        } else {
            navigate(`../${itemIds[currentIndex + 1]}`);
        }
    };

    const getGoToPreviousItem = () => {
        if (currentIndex <= 0) {
            if (props.navigatePrevious === undefined) {
                return undefined;
            } else {
                return () => props.navigatePrevious!();
            }
        } else {
            return () => navigate(`../${itemIds[currentIndex - 1]}`);
        }
    };

    let nextButtonLabel = 'Weiter';
    if (currentIndex === itemIds.length - 1 && props.showDoneButtonAfterSection) {
        nextButtonLabel = 'Fertig';
    }

    return (
        <SectionItem
            item={section.items[itemId as `item_${string}`]}
            sectionId={sectionId as `section_${string}`}
            itemId={itemId as `item_${string}`}
            onNextButtonClick={goToNextItem}
            onBackButtonClick={getGoToPreviousItem()}
            nextButtonLabel={nextButtonLabel}
        />
    );
};

export default Section;
